import { FC, Reducer, useReducer } from "react";

import * as Calculator from "./Calculator.styled";
import Slider from "./slider/Slider";
import theme from "./theme";
import { translate } from "./translations";
import { round } from "./utils";
import NumberInput from "./components/NumberInput";

interface CalculateFutureValueArgs {
  monthlyRate: number;
  interestRate: number;
  accrualsPerYear: number;
  paymentsPerYear: number;
  durationInMonths: number;
}

const calculateFutureValue = ({
  monthlyRate,
  interestRate,
  accrualsPerYear,
  paymentsPerYear,
  durationInMonths,
}: CalculateFutureValueArgs) => {
  const compoundInterestRate =
    Math.pow(
      Math.pow(interestRate / accrualsPerYear + 1, 2),
      1 / paymentsPerYear
    ) - 1;
  const totalPayments = (durationInMonths / 12) * paymentsPerYear;
  const futureValue =
    monthlyRate *
    ((Math.pow(1 + compoundInterestRate, totalPayments) - 1) /
      compoundInterestRate);

  return futureValue;
};

interface SavingsProjectionCalculatorState {
  monthlyRate: number;
  durationInMonths: number;
  interestPercentage: number;
  interestTaxPercentage: number;
  inflationPercentage: number;
  accrualsPerYear: number;
  paymentsPerYear: number;
}

type SavingsProjectionCalculatorAction =
  | {
      type: "setAmount";
      amount: number;
    }
  | {
      type: "setDuration";
      duration: number;
    }
  | {
      type: "setInterest";
      interest: number;
    }
  | {
      type: "setInterestTax";
      interestTax: number;
    }
  | {
      type: "setInflation";
      inflation: number;
    }
  | {
      type: "setAccruals";
      accruals: number;
    }
  | {
      type: "setPayments";
      payments: number;
    };

const reducer: Reducer<
  SavingsProjectionCalculatorState,
  SavingsProjectionCalculatorAction
> = (state, action) => {
  switch (action.type) {
    case "setAmount":
      return {
        ...state,
        monthlyRate: action.amount,
      };
    case "setDuration":
      return {
        ...state,
        durationInMonths: action.duration,
      };
    case "setInterest":
      return {
        ...state,
        interestPercentage: action.interest,
      };
    case "setInterestTax":
      return {
        ...state,
        interestTaxPercentage: action.interestTax,
      };
    case "setInflation":
      return {
        ...state,
        inflationPercentage: action.inflation,
      };
    case "setAccruals":
      return {
        ...state,
        accrualsPerYear: action.accruals,
      };
    case "setPayments":
      return {
        ...state,
        paymentsPerYear: action.payments,
      };
    default:
      throw new Error();
  }
};

interface SavingsProjectionCalculatorProps {
  showAccruals?: boolean;
  showPayments?: boolean;
  showInterestTax?: boolean;
  options: {
    amountMin: number;
    amountMax: number;
    amountIncrement: number;
    amountDefault: number;
    interestMin: number;
    interestMax: number;
    interestIncrement: number;
    interestDefault: number;
    interestTaxMin: number;
    interestTaxMax: number;
    interestTaxIncrement: number;
    interestTaxDefault: number;
    durationMin: number;
    durationMax: number;
    durationIncrement: number;
    durationDefault: number;
    accrualsMin: number;
    accrualsMax: number;
    accrualsIncrement: number;
    accrualsDefault: number;
    paymentsMin: number;
    paymentsMax: number;
    paymentsIncrement: number;
    paymentsDefault: number;
    inflationMin: number;
    inflationMax: number;
    inflationIncrement: number;
    inflationDefault: number;
  };
}

const SavingsProjectionCalculator: FC<SavingsProjectionCalculatorProps> = ({
  showAccruals,
  showPayments,
  showInterestTax,
  options,
}) => {
  const initialState: SavingsProjectionCalculatorState = {
    monthlyRate: options.amountDefault,
    durationInMonths: options.durationDefault,
    interestPercentage: options.interestDefault,
    interestTaxPercentage: options.interestTaxDefault,
    inflationPercentage: options.inflationDefault,
    accrualsPerYear: options.accrualsDefault,
    paymentsPerYear: options.paymentsDefault,
  };
  const [state, dispatch] = useReducer(reducer, initialState);
  const r = state.interestPercentage / 100;
  const i = state.inflationPercentage / 100;
  const t = state.interestTaxPercentage / 100;
  // If `r` is equal to `i`, we use a very small number to avoid division by zero
  const interestAfterInflation =
    r === i ? 0.00000000001 : (r + 1) / (i + 1) - 1;
  const interestAfterInterestTax = interestAfterInflation * (1 - t);

  const futureValueBeforeInterestTax = calculateFutureValue({
    monthlyRate: state.monthlyRate,
    interestRate: interestAfterInflation,
    accrualsPerYear: state.accrualsPerYear,
    paymentsPerYear: state.paymentsPerYear,
    durationInMonths: state.durationInMonths,
  });
  const futureValueAfterInterestTax = calculateFutureValue({
    monthlyRate: state.monthlyRate,
    interestRate: interestAfterInterestTax,
    accrualsPerYear: state.accrualsPerYear,
    paymentsPerYear: state.paymentsPerYear,
    durationInMonths: state.durationInMonths,
  });

  const handleLoanAmountChangeSlider = (newValue: number) => {
    dispatch({ type: "setAmount", amount: newValue });
  };

  const handleDurationChangeSlider = (newValue: number) => {
    dispatch({
      type: "setDuration",
      duration: newValue * 12,
    });
  };

  const handleInterestChangeSlider = (newValue: number) => {
    dispatch({
      type: "setInterest",
      interest: newValue,
    });
  };

  const handleInflationChangeSlider = (newValue: number) => {
    dispatch({
      type: "setInflation",
      inflation: newValue,
    });
  };

  const handleAccrualChangeSlider = (newValue: number) => {
    dispatch({
      type: "setAccruals",
      accruals: newValue,
    });
  };

  const handlePaymentChangeSlider = (newValue: number) => {
    dispatch({
      type: "setPayments",
      payments: newValue,
    });
  };

  return (
    <Calculator.Form autoComplete="off">
      <Calculator.MainContent>
        <div>
          <Calculator.Row>
            <Calculator.InputRow>
              <Calculator.Label>{translate("perMonth")}</Calculator.Label>
              <Calculator.InputRowGroup>
                <NumberInput
                  value={state.monthlyRate}
                  onChange={handleLoanAmountChangeSlider}
                  min={options.amountMin}
                  max={options.amountMax}
                  integer={true}
                  size={9}
                />
                <Calculator.UnitInputLabel>kr.</Calculator.UnitInputLabel>
              </Calculator.InputRowGroup>
            </Calculator.InputRow>
            <div style={{ width: "100%" }}>
              <Slider
                value={state.monthlyRate}
                min={options.amountMin}
                max={options.amountMax}
                step={options.amountIncrement}
                unit={{ single: "kr.", plural: "kr." }}
                onChange={handleLoanAmountChangeSlider}
              />
            </div>
          </Calculator.Row>
          <Calculator.Row>
            <Calculator.InputRow>
              <Calculator.Label>{translate("duration")}</Calculator.Label>
              <Calculator.InputRowGroup>
                <NumberInput
                  value={state.durationInMonths / 12}
                  onChange={handleDurationChangeSlider}
                  min={options.durationMin / 12}
                  max={options.durationMax / 12}
                  integer={true}
                  size={2}
                />
                <Calculator.UnitInputLabel>
                  {state.durationInMonths / 12 === 1
                    ? translate("year")
                    : translate("years")}
                </Calculator.UnitInputLabel>
              </Calculator.InputRowGroup>
            </Calculator.InputRow>
            <div style={{ width: "100%" }}>
              <Slider
                value={state.durationInMonths / 12}
                min={options.durationMin / 12}
                max={options.durationMax / 12}
                step={options.durationIncrement / 12}
                unit={{
                  single: translate("year") as string,
                  plural: translate("years") as string,
                }}
                onChange={handleDurationChangeSlider}
              />
            </div>
          </Calculator.Row>
          <Calculator.Row>
            <Calculator.InputRow>
              <Calculator.Label>{translate("interest")}</Calculator.Label>
              <Calculator.InputRowGroup>
                <NumberInput
                  value={state.interestPercentage}
                  onChange={handleInterestChangeSlider}
                  min={options.interestMin}
                  max={options.interestMax}
                  size={4}
                />
                <Calculator.UnitInputLabel>%</Calculator.UnitInputLabel>
              </Calculator.InputRowGroup>
            </Calculator.InputRow>
            <div style={{ width: "100%" }}>
              <Slider
                value={round(state.interestPercentage)}
                min={options.interestMin}
                max={options.interestMax}
                step={options.interestIncrement}
                unit={{ single: "%", plural: "%" }}
                onChange={handleInterestChangeSlider}
              />
            </div>
          </Calculator.Row>
          <Calculator.Row>
            <Calculator.InputRow>
              <Calculator.Label>{translate("inflation")}</Calculator.Label>
              <Calculator.InputRowGroup>
                <NumberInput
                  value={state.inflationPercentage}
                  onChange={handleInflationChangeSlider}
                  min={options.inflationMin}
                  max={options.inflationMax}
                  size={4}
                />
                <Calculator.UnitInputLabel>%</Calculator.UnitInputLabel>
              </Calculator.InputRowGroup>
            </Calculator.InputRow>
            <div style={{ width: "100%" }}>
              <Slider
                value={round(state.inflationPercentage)}
                min={options.inflationMin}
                max={options.inflationMax}
                step={options.inflationIncrement}
                unit={{ single: "%", plural: "%" }}
                onChange={handleInflationChangeSlider}
              />
            </div>
            <span
              style={{
                fontSize: theme.fontSize.small,
                fontWeight: "normal",
              }}
            >
              {translate("interestAfterInflation")}:&nbsp;
              <strong>
                {round(interestAfterInflation * 100).toLocaleString("da-DK")}%
              </strong>
            </span>
          </Calculator.Row>
          {showAccruals && (
            <Calculator.Row>
              <Calculator.InputRow>
                <Calculator.Label>
                  {translate("accrualsPerYear")}
                </Calculator.Label>
                <Calculator.InputRowGroup>
                  <NumberInput
                    value={state.accrualsPerYear}
                    onChange={handleAccrualChangeSlider}
                    min={options.accrualsMin}
                    max={options.accrualsMax}
                    size={2}
                  />
                  <Calculator.UnitInputLabel>
                    {state.accrualsPerYear === 1
                      ? translate("time")
                      : translate("times")}
                  </Calculator.UnitInputLabel>
                </Calculator.InputRowGroup>
              </Calculator.InputRow>
              <div style={{ width: "100%" }}>
                <Slider
                  value={state.accrualsPerYear}
                  min={options.accrualsMin}
                  max={options.accrualsMax}
                  step={options.accrualsIncrement}
                  unit={{
                    single: translate("time") as string,
                    plural: translate("times") as string,
                  }}
                  onChange={handleAccrualChangeSlider}
                />
              </div>
            </Calculator.Row>
          )}
          {showPayments && (
            <Calculator.Row>
              <Calculator.InputRow>
                <Calculator.Label>
                  {translate("paymentsPerYear")}
                </Calculator.Label>
                <Calculator.InputRowGroup>
                  <NumberInput
                    value={state.paymentsPerYear}
                    onChange={handlePaymentChangeSlider}
                    min={options.paymentsMin}
                    max={options.paymentsMax}
                    size={3}
                  />
                  <Calculator.UnitInputLabel>
                    {state.accrualsPerYear === 1
                      ? translate("time")
                      : translate("times")}
                  </Calculator.UnitInputLabel>
                </Calculator.InputRowGroup>
              </Calculator.InputRow>
              <div style={{ width: "100%" }}>
                <Slider
                  value={state.paymentsPerYear}
                  min={options.paymentsMin}
                  max={options.paymentsMax}
                  step={options.paymentsIncrement}
                  unit={{
                    single: translate("time") as string,
                    plural: translate("times") as string,
                  }}
                  onChange={handlePaymentChangeSlider}
                />
              </div>
            </Calculator.Row>
          )}
        </div>
      </Calculator.MainContent>
      <Calculator.SecondaryContent>
        <Calculator.Row>
          <Calculator.Label $result>
            {translate("savingsInXYears", { x: state.durationInMonths / 12 })}
          </Calculator.Label>
          <span
            style={{
              fontSize: theme.fontSize.huge,
              fontWeight: "bold",
              whiteSpace: "nowrap",
            }}
          >
            {(
              Math.round(futureValueBeforeInterestTax * 100) / 100
            ).toLocaleString("da-DK")}{" "}
            kr.
          </span>
        </Calculator.Row>
        {showInterestTax && state.interestTaxPercentage > 0 && (
          <details
            open
            style={{
              border: `1px solid ${theme.color.darkGray}`,
              backgroundColor: "rgba(255, 255, 255, 0.75)",
              padding: "5px 10px",
              borderRadius: theme.radius.medium,
              marginTop: theme.space.small,
            }}
          >
            <summary style={{ cursor: "pointer", userSelect: "none" }}>
              {translate("interestTax")}
            </summary>
            <Calculator.Row>
              <Calculator.Label $result>
                {translate("savingsAfterXPercentInterestTax", {
                  x: state.interestTaxPercentage,
                })}
              </Calculator.Label>
              <span
                style={{
                  fontSize: theme.fontSize.medium,
                  whiteSpace: "nowrap",
                }}
              >
                {(
                  Math.round(futureValueAfterInterestTax * 100) / 100
                ).toLocaleString("da-DK")}{" "}
                kr.
              </span>
            </Calculator.Row>
          </details>
        )}
      </Calculator.SecondaryContent>
    </Calculator.Form>
  );
};

export default SavingsProjectionCalculator;
